import type {Suggestions} from '@github-ui/inline-autocomplete/types'
import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {PullRequestSuggestion} from './PullRequestSuggestion'

export interface PullRequestsQuery {
  category: 'pulls'
  repository: string
  filter: string
}

export function usePullRequestsSuggestions(query: PullRequestsQuery | null) {
  const {service} = useChatManager()

  const pullRequestsQuery = useQuery({
    enabled: query !== null,
    queryKey: ['copilot-autocomplete-pulls', query?.repository, query?.filter],
    queryFn: async () => service.fetchAutocompletePullRequests(query!.repository, query!.filter),
    placeholderData: prev => prev,
  })

  return useMemo<Suggestions | null>(() => {
    if (!query) return null

    if (pullRequestsQuery.isLoading) return 'loading'

    return (
      pullRequestsQuery.data?.map(pull => ({
        value: ReferenceMention.stringify({type: 'pull-request', repo: query.repository, id: pull.number.toString()}),
        key: pull.number.toString(),
        render: props => (
          <PullRequestSuggestion {...props} pullRequest={pull} stale={pullRequestsQuery.isPlaceholderData} />
        ),
      })) ?? null
    )
  }, [pullRequestsQuery.data, pullRequestsQuery.isLoading, pullRequestsQuery.isPlaceholderData, query])
}
